/**
 * Custom hook for service worker management and PWA functionality
 * Part of the FAANG-grade _app.tsx refactoring - Phase 2.2
 */

import { useEffect } from 'react';

/**
 * Hook for managing service worker registration and PWA features
 */
export const useServiceWorker = () => {
  // NOTE: No SSR guard before hooks — useEffect only runs on client.
  // An early return here would break React's rules of hooks (error #300).

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    // =============================================================================
    // PWA SERVICE WORKER - DISABLED FOR STABLE RELEASE
    // =============================================================================
    // The service worker was disabled to fix Apache rewrite loading issues during
    // the initial launch phase. This prevents the /sw.js 404 error.
    //
    // TODO-POST-LAUNCH: Re-enable PWA after code cleanup is complete.
    //   - This is a post-launch enhancement, not a launch blocker.
    //   - PWA features (offline access, push notifications, logo caching) are
    //     nice-to-have but not critical for marketplace launch.
    //   - Keeping SW disabled prevents it from hiding potential code issues
    //     during the critical launch stabilization period.
    //
    // To re-enable:
    //   1. Add to .env.local and .env.production: NEXT_PUBLIC_PWA_ENABLED=true
    //   2. Uncomment the withPWA plugin in next.config.cjs
    //   3. Test offline flow, push notifications, and logo caching
    //   4. Ensure Apache rewrite rules work correctly with SW
    // =============================================================================
    if (process.env.NEXT_PUBLIC_PWA_ENABLED !== 'true') {
      console.log(
        '[SW] PWA disabled (NEXT_PUBLIC_PWA_ENABLED !== "true") - skipping service worker registration',
      );
      return;
    }

    const IS_W11 = process.env.SMOKE_HARNESS_WEEK === 'Week 11';

    // Register service worker for logo caching (Chunk 12)
    try {
      navigator.serviceWorker
        .register('/sw.js', { scope: '/' })
        .then((registration) => {
          console.log('[SW] Logo caching service worker registered:', registration.scope);
        })
        .catch((error) => {
          console.warn('[SW] Logo caching service worker registration failed:', error);
        });
    } catch (error) {
      console.warn('[SW] Logo caching service worker registration error:', error);
    }

    // Early service worker registration for Week 11 (additional)
    if (IS_W11) {
      // Already registered above
    }
  }, []);

  useEffect(() => {
    // Service worker ready check for Week 11
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;

    const IS_W11 = process.env.SMOKE_HARNESS_WEEK === 'Week 11';
    const w11Public = /^\/(pwa(\/|$)|crm\/pipeline$|services(\/|$)|quotes(\/|$))/.test(
      window.location.pathname,
    );

    if (!IS_W11 || !w11Public) return;

    (async () => {
      try {
        await navigator.serviceWorker.ready;
        if (!navigator.serviceWorker.controller && !sessionStorage.getItem('__sw_reloaded')) {
          sessionStorage.setItem('__sw_reloaded', '1');
          console.log('[SW] Reloading page to activate service worker');
          location.reload();
        }
      } catch (error) {
        console.warn('[SW] Ready check failed:', error);
      }
    })();
  }, []);

  useEffect(() => {
    // Push manager patching for PWA notifications
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;

    (async () => {
      try {
        const registration = await navigator.serviceWorker.ready;
        const pushManager: any = registration?.pushManager;

        if (pushManager && !pushManager.__patched) {
          const originalSubscribe = pushManager.subscribe?.bind(pushManager);

          pushManager.subscribe = async (options: any) => {
            try {
              if (originalSubscribe) {
                const subscription = await originalSubscribe(options);

                // Post push subscription once per session after successful subscribe
                if (navigator.onLine && !sessionStorage.getItem('__pwa_push_posted')) {
                  const json = (subscription as any)?.toJSON
                    ? (subscription as any).toJSON()
                    : subscription;
                  const payload = json?.endpoint ? json : { endpoint: '/push' };

                  try {
                    await fetch('/api/pwa/push', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify(payload),
                    });
                    sessionStorage.setItem('__pwa_push_posted', '1');
                    console.log('[SW] Push subscription posted to server');
                  } catch (error) {
                    console.warn('[SW] Failed to post push subscription:', error);
                  }
                }

                return subscription;
              }
            } catch (error) {
              // Handle offline scenarios and missing VAPID
              const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;

              if (isOffline) {
                // While offline, subscribe must fail for tests
                throw error;
              }

              // Online but subscribe failed - return minimal fake subscription
              console.log('[SW] Using fake push subscription (VAPID not configured)');
              const fakeSubscription = {
                endpoint: '/push',
                keys: { p256dh: 'stub', auth: 'stub' },
              };

              // Still try to post the fake subscription
              if (!sessionStorage.getItem('__pwa_push_posted')) {
                try {
                  await fetch('/api/pwa/push', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(fakeSubscription),
                  });
                  sessionStorage.setItem('__pwa_push_posted', '1');
                } catch (postError) {
                  console.warn('[SW] Failed to post fake subscription:', postError);
                }
              }

              return fakeSubscription;
            }
          };

          pushManager.__patched = true;
          console.log('[SW] Push manager patched for PWA support');
        }
      } catch (error) {
        console.warn('[SW] Push manager patching failed:', error);
      }
    })();
  }, []);
};
